import { Link, Navigate, useParams } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowRight, ArrowUpRight, Check, FileDown, Mail, Phone } from "lucide-react";
import { CASSETTES } from "@/data/cassettes";
import { findCategory } from "@/data/products";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang, EN_CATEGORY_LABELS } from "@/i18n";
import { EN_CATEGORIES, EN_CASSETTES, EN_TAGS } from "@/data/productsEn";

export default function CategoryPage() {
    const { slug } = useParams();
    const cat = findCategory(slug);
    const { lang, t } = useLang();
    const en = cat ? EN_CATEGORIES[cat.slug] : null;
    const catLabel = cat ? t(cat.label, EN_CATEGORY_LABELS[cat.slug] || cat.label) : "";
    useSeo(
        cat ? `${catLabel} | Müller Satelliten- und Antennentechnik` : `${t("Produkte", "Products")} | Müller`,
        cat ? t(cat.short, en?.short ?? cat.short) : "Produktübersicht der Müller Satelliten- und Antennentechnik.",
    );
    // Englische Textebene: Items nach Position (items[] mit Namen oder itemDescs[] nur Beschreibungen)
    const enItem = (i) => en?.items?.[i] ?? null;
    const itemName = (it, i) => (enItem(i)?.name != null ? t(it.name, enItem(i).name) : it.name);
    const itemDesc = (it, i) =>
        enItem(i)?.desc != null ? t(it.desc, enItem(i).desc) : t(it.desc, en?.itemDescs?.[i] ?? it.desc);
    const casDesc = (c) => (lang === "en" ? EN_CASSETTES[c.code] ?? c.desc : c.desc);
    const enFeatureList = lang === "en" && en?.features ? en.features : cat?.features;

    if (!cat) return <Navigate to="/produkte" replace />;

    const isSkuList = cat.items?.length > 0 && !!cat.items[0].sku;
    const pdfBanners = cat.pdf ? (Array.isArray(cat.pdf) ? cat.pdf : [cat.pdf]) : [];

    const galleryBlock = cat.gallery ? (
        <section className="pb-16 sm:pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <Reveal>
                    <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
                        {t(cat.gallery.title, en?.gallery?.title ?? cat.gallery.title)}
                    </h2>
                </Reveal>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cat.gallery.items.map((g, i) => (
                        <Reveal key={g.src} delay={(i % 3) * 0.06} className={g.wide ? "sm:col-span-2" : ""}>
                            <figure
                                className="h-full rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col"
                                data-testid={`gallery-item-${i}`}
                            >
                                <div className={`${cat.galleryCompact ? "h-36 sm:h-44" : "h-52 sm:h-56"} ${g.tall ? "sm:h-72" : ""} flex items-center justify-center bg-white overflow-hidden p-3`}>
                                    <img
                                        src={g.src}
                                        alt={t(g.caption, en?.gallery?.captions?.[i] ?? g.caption)}
                                        loading="lazy"
                                        className="max-h-full max-w-full object-contain rounded"
                                    />
                                </div>
                                <figcaption className="px-4 py-3 text-xs text-slate-600 border-t border-slate-100 leading-relaxed">
                                    {t(g.caption, en?.gallery?.captions?.[i] ?? g.caption)}
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    ) : null;

    return (
        <main data-testid="page-category" className="pt-28 sm:pt-32">
            <section className="pb-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <p className="font-mono text-[11px] uppercase tracking-widest text-slate-400" data-testid="breadcrumb">
                        <Link to="/produkte" data-testid="breadcrumb-produkte" className="hover:text-red-700 transition-colors">
                            {t("Produkte", "Products")}
                        </Link>
                        <span className="mx-2">/</span>
                        <span className="text-slate-600">{catLabel}</span>
                    </p>
                    <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
                        <div className={cat.img ? "lg:col-span-7" : "lg:col-span-12"}>
                            <Reveal>
                                <Eyebrow>{t(cat.tag, EN_TAGS[cat.tag] || cat.tag)}</Eyebrow>
                                <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                                    {catLabel}
                                </h1>
                                {cat.intro.map((p, i) => (
                                    <p key={p.slice(0, 24)} className="mt-4 text-slate-600 leading-relaxed text-sm sm:text-base max-w-3xl">
                                        {t(p, en?.intro?.[i] ?? p)}
                                    </p>
                                ))}
                                {enFeatureList && (
                                    <ul className="mt-6 space-y-3" data-testid="category-features">
                                        {enFeatureList.map((f) => (
                                            <li key={f.t} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                                                <Check className="h-4 w-4 mt-1 shrink-0 text-emerald-600" />
                                                <span>
                                                    <strong className="font-semibold">{f.t}:</strong> {f.d}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Reveal>
                        </div>
                        {cat.img && (
                            <Reveal delay={0.15} className="lg:col-span-5">
                                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg aspect-[4/3]">
                                    <img
                                        src={cat.img}
                                        alt={cat.label}
                                        className={`h-full w-full ${cat.imgFit === "contain" ? "object-contain bg-white p-3" : "object-cover"}`}
                                    />
                                </div>
                            </Reveal>
                        )}
                    </div>
                </div>
            </section>

            {pdfBanners.length > 0 && (
                <section className="pb-2">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                        {pdfBanners.map((p, i) => (
                            <Reveal key={p.url}>
                                <a
                                    href={p.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`category-pdf-banner-${i}`}
                                    className="group mb-4 flex items-center gap-4 rounded-xl border-2 border-red-100 bg-red-50/50 hover:border-red-300 px-5 py-4 transition-colors"
                                >
                                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-red-100 bg-white text-red-700 shadow-sm">
                                        <FileDown className="h-5 w-5" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <p className="font-display text-sm font-bold text-slate-900 sm:text-base">{t(p.de.title, p.en.title)}</p>
                                        <p className="mt-0.5 text-sm text-slate-600">{t(p.de.sub, p.en.sub)}</p>
                                    </div>
                                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-700 px-4 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-red-800">
                                        {t("PDF öffnen", "Open PDF")}
                                        <FileDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                                    </span>
                                </a>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {cat.gallery && cat.galleryTop && galleryBlock}
            {cat.items?.length > 0 || cat.groups?.length > 0 || cat.links?.length > 0 || cat.dhx || cat.showCassettes ? (
                <section className="pb-16 sm:pb-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                        {cat.dhx && (
                            <Reveal>
                                <div className="mt-10" data-testid="dhx-section">
                                    <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
                                        {t("Unsere Kopfstationen: die Müller DHx-Basis.", "Our headends: the Müller DHx-Basis.")}
                                    </h2>
                                    <p className="mt-2 text-sm text-slate-600 max-w-3xl">
                                        {t(
                                            "Drei Baugrößen, ein Baukasten: Jede DHx-Basis wird von uns bestückt, programmiert und getestet — mit 4 Jahren Garantie.",
                                            "Three sizes, one modular system: every DHx-Basis is assembled, programmed and tested by us — with a 4-year warranty."
                                        )}
                                    </p>
                                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {cat.dhx.map((d, i) => (
                                            <div
                                                key={d.name}
                                                className="relative h-full rounded-xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col"
                                                data-testid={`dhx-card-${i}`}
                                            >
                                                <img
                                                    src="/produkte/garantie-4jahre.webp"
                                                    alt="4 Jahre Garantie"
                                                    loading="lazy"
                                                    className="absolute top-3 right-3 h-10 w-auto -rotate-6 opacity-90"
                                                />
                                                <div className="h-40 sm:h-44 flex items-center justify-center bg-white overflow-hidden p-4 border-b border-slate-100">
                                                    {d.img ? (
                                                        <img src={d.img} alt={`Müller ${d.name}`} loading="lazy" className="max-h-full max-w-full object-contain" />
                                                    ) : (
                                                        <span className="font-mono text-4xl font-bold text-slate-300">DH2</span>
                                                    )}
                                                </div>
                                                <div className="p-5">
                                                    <p className="font-mono text-[11px] text-red-700">{d.name}</p>
                                                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t(d.desc, en?.dhx?.[i] ?? d.desc)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        )}
                        {cat.showCassettes && (
                            <Reveal>
                                <div className="mt-10" data-testid="cassette-table">
                                    <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
                                        {t("Unsere Steckkarten (Kassetten) für die DHx-Basis.", "Our plug-in cards (cassettes) for the DHx-Basis.")}
                                    </h2>
                                    <p className="mt-2 text-sm text-slate-600 max-w-3xl">
                                        {t(
                                            "Alle Kassetten passen in DH8-, DH4- und DH2-Basis — die Typenbezeichnung verrät Ausbaustufe und Ausgang. Kombinieren Sie Ihre Bestückung direkt im Konfigurator.",
                                            "All cassettes fit the DH8-, DH4- and DH2-Basis — the type designation reveals the expansion stage and output. Configure your assembly directly in the configurator."
                                        )}
                                    </p>
                                    {CASSETTES.filter((c) => c.img).length > 0 && (
                                        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5" data-testid="cassette-image-cards">
                                            {CASSETTES.filter((c) => c.img).map((c) => (
                                                <div key={c.code} className="h-full rounded-xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col" data-testid={`cassette-image-${c.code.toLowerCase()}`}>
                                                    <div className="h-28 flex items-center justify-center overflow-hidden mb-3">
                                                        <img src={c.img} alt={`Müller Steckkarte ${c.code}`} loading="lazy" className="max-h-full max-w-full object-contain rounded" />
                                                    </div>
                                                    <p className="font-mono text-[11px] text-red-700">{c.code}</p>
                                                    <p className="mt-1.5 text-xs text-slate-600 leading-relaxed">{casDesc(c)}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 shadow-sm bg-white">
                                        <table className="w-full text-sm min-w-[560px]">
                                            <thead>
                                                <tr className="bg-slate-50 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
                                                    <th className="px-5 py-3 font-medium">{t("Art.-Nr.", "Item no.")}</th>
                                                    <th className="px-5 py-3 font-medium">{t("Kassette", "Cassette")}</th>
                                                    <th className="px-5 py-3 font-medium">{t("Beschreibung", "Description")}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {CASSETTES.filter((c) => c.code !== "FREI").map((c) => (
                                                    <tr key={c.code} className="hover:bg-slate-50/70 transition-colors">
                                                        <td className="px-5 py-3.5 font-mono text-red-700 whitespace-nowrap">{c.sku}</td>
                                                        <td className="px-5 py-3.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{c.code}</td>
                                                        <td className="px-5 py-3.5 text-slate-600">{casDesc(c)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Reveal>
                        )}
                        {isSkuList ? (
                            <Reveal>
                                <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid="category-sku-list">
                                    <div className="divide-y divide-slate-100">
                                        {cat.items.map((it, i) => (
                                            <div key={it.sku} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 px-5 sm:px-7 py-4 hover:bg-slate-50 transition-colors">
                                                <span className="shrink-0 w-fit font-mono text-[11px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">
                                                    {it.sku}
                                                </span>
                                                <span className="shrink-0 sm:w-52 font-display font-bold text-sm text-slate-900">{it.name}</span>
                                                <span className="text-sm text-slate-600 leading-relaxed flex-1">{itemDesc(it, i)}</span>
                                                {it.ds && (
                                                    <a
                                                        href={it.ds}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`sku-datasheet-${it.sku}`}
                                                        className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5 w-fit"
                                                    >
                                                        <FileDown className="h-3.5 w-3.5" />
                                                        {t("Datenblatt (PDF)", "Datasheet (PDF)")}
                                                    </a>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Reveal>
                        ) : cat.groups ? (
                            cat.groups.map((g, gi) => {
                                const gen = en?.groups?.[gi];
                                const gName = (it, i) => {
                                    const e = gen?.items?.[i];
                                    return e?.name != null ? t(it.name, e.name) : it.name;
                                };
                                const gDesc = (it, i) => {
                                    const e = gen?.items?.[i];
                                    return e?.desc != null ? t(it.desc, e.desc) : t(it.desc, gen?.itemDescs?.[i] ?? it.desc);
                                };
                                return (
                                    <div key={g.title} className={gi > 0 ? "mt-12" : "mt-2"} data-testid={`category-group-${gi}`}>
                                        <Reveal>
                                            <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900">
                                                {t(g.title, gen?.title ?? g.title)}
                                            </h2>
                                            {(g.text || gen?.text) && (
                                                <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-3xl">
                                                    {t(g.text ?? gen.text, gen?.text ?? g.text)}
                                                </p>
                                            )}
                                        </Reveal>
                                        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {g.items.map((it, i) => {
                                                const inner = (
                                                    <>
                                                        {it.img && (
                                                            <div className="mb-4 flex h-36 items-center justify-center overflow-hidden">
                                                                <img
                                                                    src={it.img}
                                                                    alt={it.name}
                                                                    loading="lazy"
                                                                    className="max-h-full max-w-full object-contain"
                                                                />
                                                            </div>
                                                        )}
                                                        <h3 className="font-display font-bold text-slate-900">{gName(it, i)}</h3>
                                                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{gDesc(it, i)}</p>
                                                        {it.pdf && (
                                                            <a
                                                                href={it.pdf.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-testid={`group-${gi}-item-pdf-${i}`}
                                                                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5"
                                                            >
                                                                <FileDown className="h-3.5 w-3.5" />
                                                                {t(it.pdf.de.title, it.pdf.en.title)}
                                                            </a>
                                                        )}
                                                    </>
                                                );
                                                const cls = "h-full rounded-xl bg-white border border-slate-200 p-6 shadow-sm";
                                                return it.to ? (
                                                    <Reveal key={it.name} delay={(i % 3) * 0.06}>
                                                        <Link
                                                            to={it.to}
                                                            data-testid={`group-${gi}-item-${i}`}
                                                            className={`group block ${cls} hover:border-red-300 hover:shadow-md transition-all`}
                                                        >
                                                            <div className="flex items-center justify-between gap-3">
                                                                <h3 className="font-display font-bold text-slate-900 group-hover:text-red-800 transition-colors">{gName(it, i)}</h3>
                                                                <ArrowRight className="h-4 w-4 text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                                                            </div>
                                                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{gDesc(it, i)}</p>
                                                        </Link>
                                                    </Reveal>
                                                ) : (
                                                    <Reveal key={it.name} delay={(i % 3) * 0.06}>
                                                        <div className={cls} data-testid={`group-${gi}-item-${i}`}>
                                                            {inner}
                                                        </div>
                                                    </Reveal>
                                                );
                                            })}
                                        </div>
                                        {g.link && (
                                            <Reveal>
                                                {g.link.to.startsWith("http") ? (
                                                    <a
                                                        href={g.link.to}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`group-${gi}-link`}
                                                        className="group mt-5 flex items-center justify-between gap-4 rounded-xl border-2 border-red-100 bg-red-50/50 hover:border-red-300 px-5 py-4 transition-colors"
                                                    >
                                                        <div>
                                                            <p className="font-display font-bold text-slate-900 text-sm sm:text-base">{t(g.link.label, gen?.link?.label ?? g.link.label)}</p>
                                                            <p className="text-sm text-slate-600 mt-0.5">{t(g.link.desc, gen?.link?.desc ?? g.link.desc)}</p>
                                                        </div>
                                                        <ArrowUpRight className="h-5 w-5 text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                                                    </a>
                                                ) : (
                                                    <Link
                                                        to={g.link.to}
                                                        data-testid={`group-${gi}-link`}
                                                        className="group mt-5 flex items-center justify-between gap-4 rounded-xl border-2 border-red-100 bg-red-50/50 hover:border-red-300 px-5 py-4 transition-colors"
                                                    >
                                                        <div>
                                                            <p className="font-display font-bold text-slate-900 text-sm sm:text-base">{t(g.link.label, gen?.link?.label ?? g.link.label)}</p>
                                                            <p className="text-sm text-slate-600 mt-0.5">{t(g.link.desc, gen?.link?.desc ?? g.link.desc)}</p>
                                                        </div>
                                                        <ArrowRight className="h-5 w-5 text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                                                    </Link>
                                                )}
                                            </Reveal>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {cat.items.map((it, i) => {
                                    const inner = (
                                        <>
                                            {it.img && (
                                                <div className="mb-4 flex h-36 items-center justify-center overflow-hidden">
                                                    <img
                                                        src={it.img}
                                                        alt={it.name}
                                                        loading="lazy"
                                                        className="max-h-full max-w-full object-contain"
                                                    />
                                                </div>
                                            )}
                                            <h3 className="font-display font-bold text-slate-900">{itemName(it, i)}</h3>
                                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{itemDesc(it, i)}</p>
                                            {it.pdf && (
                                                <a
                                                    href={it.pdf.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-testid={`item-pdf-${i}`}
                                                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 hover:text-red-800 border-b border-red-300 pb-0.5"
                                                >
                                                    <FileDown className="h-3.5 w-3.5" />
                                                    {t(it.pdf.de.title, it.pdf.en.title)}
                                                </a>
                                            )}
                                        </>
                                    );
                                    const cls = "h-full rounded-xl bg-white border border-slate-200 p-6 shadow-sm";
                                    return it.to ? (
                                        <Reveal key={it.name} delay={(i % 3) * 0.06}>
                                            <Link
                                                to={it.to}
                                                data-testid={`category-item-${i}`}
                                                className={`group block ${cls} hover:border-red-300 hover:shadow-md transition-all`}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <h3 className="font-display font-bold text-slate-900 group-hover:text-red-800 transition-colors">{itemName(it, i)}</h3>
                                                    <ArrowRight className="h-4 w-4 text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                                                </div>
                                                <p className="mt-2 text-sm leading-relaxed text-slate-600">{itemDesc(it, i)}</p>
                                            </Link>
                                        </Reveal>
                                    ) : (
                                        <Reveal key={it.name} delay={(i % 3) * 0.06}>
                                            <div className={cls} data-testid={`category-item-${i}`}>
                                                {inner}
                                            </div>
                                        </Reveal>
                                    );
                                })}
                            </div>
                        )}
                        {cat.note && (
                            <Reveal>
                                <p className="mt-6 text-sm text-slate-500 flex items-center gap-2">
                                    <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                                    {t(cat.note, en?.note ?? cat.note)}
                                </p>
                            </Reveal>
                        )}
                        {cat.links?.map((l, i) => (
                            <Reveal key={l.to} delay={i * 0.05}>
                                <Link
                                    to={l.to}
                                    data-testid={`category-more-link-${i}`}
                                    className="group mt-4 first:mt-6 flex items-center justify-between gap-4 rounded-xl border-2 border-red-100 bg-red-50/50 hover:border-red-300 px-5 py-4 transition-colors"
                                >
                                    <div>
                                        <p className="font-display font-bold text-slate-900 text-sm sm:text-base">{t(l.label, en?.links?.[i]?.label ?? l.label)}</p>
                                        <p className="text-sm text-slate-600 mt-0.5">{t(l.desc, en?.links?.[i]?.desc ?? l.desc)}</p>
                                    </div>
                                    <ArrowRight className="h-5 w-5 text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </section>
            ) : null}

            {cat.gallery && !cat.galleryTop && galleryBlock}

                <section className="pb-20 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <div className="rounded-2xl bg-slate-950 text-white p-8 sm:p-12 relative overflow-hidden" data-testid="category-cta">
                            <div className="absolute inset-0 bg-blueprint-dark opacity-60" aria-hidden="true" />
                            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                                <div>
                                    <p className="eyebrow text-red-300">{t("Beratung & Bestellung", "Advice & ordering")}</p>
                                    <h2 className="mt-3 font-display text-xl sm:text-2xl font-bold">
                                        {t(`Fragen zu ${cat.label}? Wir beraten Sie gern.`, `Questions about ${catLabel}? We'll be happy to advise you.`)}
                                    </h2>
                                    <p className="mt-2 text-slate-400 text-sm max-w-xl">
                                        {t(
                                            "Direkter Draht zu unseren Technikern — mit Auslegung, Preisliste und Lieferung deutschlandweit & ins EU-Ausland.",
                                            "A direct line to our technicians — with design, price lists and delivery throughout Germany & the EU."
                                        )}
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-3 shrink-0">
                                    <a
                                        href="tel:+49919594040"
                                        data-testid="category-phone-cta"
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-red-50 font-semibold text-sm px-6 py-3 transition-colors"
                                    >
                                        <Phone className="h-4 w-4" />
                                        +49 (0) 9195 / 9404-0
                                    </a>
                                    <a
                                        href={`mailto:vertrieb@satmueller.de?subject=${encodeURIComponent(`Anfrage: ${cat.label}`)}`}
                                        data-testid="category-mail-cta"
                                        className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-white/60 font-semibold text-sm px-6 py-3 transition-colors"
                                    >
                                        <Mail className="h-4 w-4" />
                                        {t("Anfrage senden", "Send request")}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                    <div className="mt-8">
                        <Link
                            to="/produkte"
                            data-testid="back-to-products"
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-red-700 transition-colors"
                        >
                            <ArrowUpRight className="h-4 w-4 rotate-180" />
                            {t("Alle Produktkategorien im Überblick", "All product categories at a glance")}
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
